import React, { ReactElement, useCallback, useContext, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'

import cx from 'classnames'

import { ActionDropdown, Button, Div, IconButton, ToolTip } from '@driftt/tide-core'

import { EnrollModal } from 'components/EnrollModal'
import { isEU } from 'config/index'
import { isRunningInDesktop } from 'config/platform'
import { EnabledIntegrationsContext } from 'context/enabledIntegrations'
import { Insight } from 'modules/accountActivity/domain'
import { ActivityEndUser, CompanyData } from 'modules/accountActivity/domain'
import { AccountDetailsPageParams } from 'pages/CCRMAccountDetailsPage'
import { pluralize } from 'utils/formatStrings'
import { buildLinkedInSearchUrl } from 'utils/linkedin'
import { visitorUrl } from 'utils/paths'
import { accountPath, contactPath } from 'utils/paths'
import { segmentTrack, TrackType } from 'utils/tracking'

import { ReactComponent as OutreachEnrolledIcon } from '../../svgs/outreach-enrolled.svg'
import { ReactComponent as SalesloftEnrolledIcon } from '../../svgs/salesloft-enrolled.svg'

import styles from './styles.module.css'

export enum ActionType {
  EMAIL = 'email',
  LINKEDIN = 'linkedin',
  CONTACT_INFO = 'info',
  OUTREACH = 'outreach',
  SALESLOFT = 'salesloft',
  CHAT = 'message-1'
}

interface ActivityAction {
  type: ActionType
  tooltipLabel: string
  overflowLabel: string
  overflowDescription?: string
  disabled?: boolean
  actionIcon?: ReactElement
  onClick?: () => void
}

const NO_EMAIL_ADDRESS_MESSAGE = "We don't have an email address for this site visitor"
const GMAIL_BASE_URL = 'https://mail.google.com/mail/?view=cm&fs=1'

const getEmailParts = (endUserData: ActivityEndUser | undefined) => {
  const email = endUserData?.email || ''
  const subject = endUserData?.name
    ? `${endUserData?.name}, I noticed you stopping by`
    : `I noticed you stopping by`
  const emailNewLine = '%0d%0a'
  const body = endUserData?.name ? `Hey ${endUserData?.name},${emailNewLine}` : ``

  return { email, subject, body }
}

const buildActivityAction = ({
  accountId,
  insight,
  endUserData,
  companyData,
  type,
  onEnrollClick,
  onViewContactInfo,
  numEnrolledOutreachSequences,
  numEnrolledSalesloftCadences,
  outreachEnabled,
  trackAction
}: {
  accountId: string
  insight?: Insight
  endUserData: ActivityEndUser | undefined
  companyData: CompanyData | undefined
  type: ActionType | undefined
  onEnrollClick: (provider: string) => void
  onViewContactInfo: (endUserData: ActivityEndUser) => void
  numEnrolledOutreachSequences?: number
  numEnrolledSalesloftCadences?: number
  outreachEnabled?: boolean
  trackAction?: (action: string, additionalData?: any) => void
}): ActivityAction | null => {
  const canShowOutreachItems = !isEU && outreachEnabled
  const canShowSalesloftItems = !isEU

  const insightCardPage = window.location.href.includes('insights')

  const insightTrack = (trackType: TrackType) => {
    segmentTrack(trackType, {
      accountId: insight?.accountId || accountId,
      insightId: insight?.id,
      contactEmail: endUserData?.email,
      companyName: companyData?.name
    })
  }

  const onLinkedInClick = (url: string) => {
    window.open(url)
    insightTrack(
      insight
        ? insightCardPage
          ? TrackType.INSIGHT_CARD_PAGE_LINKEDIN_CLICK
          : TrackType.INSIGHT_FEED_LINKEDIN_CLICK
        : TrackType.CONTACT_LIST_LINKEDIN_CLICK
    )
    trackAction && trackAction('Contact LinkedIn')
  }

  const onEmailClick = (url: string) => {
    window.open(url)
    insightTrack(
      insight
        ? insightCardPage
          ? TrackType.INSIGHT_CARD_PAGE_EMAIL_CLICK
          : TrackType.INSIGHT_FEED_EMAIL_CLICK
        : TrackType.CONTACT_LIST_EMAIL_CLICK
    )
    trackAction && trackAction('Contact Email')
  }

  const onChatClick = (url: string) => {
    insightTrack(
      insightCardPage ? TrackType.INSIGHT_CARD_PAGE_CHAT_CLICK : TrackType.INSIGHT_FEED_CHAT_CLICK
    )
    trackAction && trackAction('Contact Chat')
    if (isRunningInDesktop) {
      window.location.href = url
    } else {
      window.open(url)
    }
  }

  const onContactInfoClick = (endUserData: ActivityEndUser) => {
    onViewContactInfo(endUserData)
    insightTrack(TrackType.CONTACT_LIST_CONTACT_INFO_CLICK)
  }

  const onOutreachEnrollClick = () => {
    onEnrollClick(ActionType.OUTREACH)
    insightTrack(
      insight
        ? insightCardPage
          ? TrackType.INSIGHT_CARD_PAGE_OUTREACH_ENROLL_CLICK
          : TrackType.INSIGHT_FEED_OUTREACH_ENROLL_CLICK
        : TrackType.CONTACT_LIST_OUTREACH_ENROLL_CLICK
    )
    trackAction && trackAction('Contact Outreach Enroll')
  }

  const onSalesloftEnrollClick = () => {
    onEnrollClick(ActionType.SALESLOFT)
    insightTrack(
      insight
        ? insightCardPage
          ? TrackType.INSIGHT_CARD_PAGE_SALESLOFT_ENROLL_CLICK
          : TrackType.INSIGHT_FEED_SALESLOFT_ENROLL_CLICK
        : TrackType.CONTACT_LIST_SALESLOFT_ENROLL_CLICK
    )
    trackAction && trackAction('Contact Salesloft Enroll')
  }

  if (type === ActionType.EMAIL) {
    if (endUserData?.email) {
      const { email, subject, body } = getEmailParts(endUserData)
      const params = new URLSearchParams({ to: email, su: subject, body })
      const mailToLink = `${GMAIL_BASE_URL}&${params.toString()}`
      return {
        type,
        tooltipLabel: 'Send an email',
        overflowLabel: 'Send an email',
        onClick: () => onEmailClick(mailToLink)
      }
    } else {
      return {
        type,
        tooltipLabel: NO_EMAIL_ADDRESS_MESSAGE,
        overflowLabel: 'Send an email',
        overflowDescription: NO_EMAIL_ADDRESS_MESSAGE,
        disabled: true
      }
    }
  }

  if (type === ActionType.LINKEDIN) {
    const linkedInUrl = buildLinkedInSearchUrl(endUserData, companyData)
    return {
      type,
      tooltipLabel: 'Search on LinkedIn',
      overflowLabel: 'Search on LinkedIn',
      actionIcon: (
        <IconButton
          icon={type.toString()}
          className={styles.linkedinDark}
          onClick={() => onLinkedInClick(linkedInUrl)}
        />
      ),
      onClick: () => onLinkedInClick(linkedInUrl)
    }
  }
  if (type === ActionType.CHAT) {
    return {
      type,
      tooltipLabel: 'Start a chat',
      overflowLabel: 'Start a chat',
      actionIcon: (
        <IconButton
          onClick={() => onChatClick(visitorUrl(endUserData?.id))}
          icon={type.toString()}
        />
      )
    }
  }
  if (type === ActionType.CONTACT_INFO && endUserData) {
    return {
      type,
      tooltipLabel: 'View contact',
      overflowLabel: 'View contact',
      onClick: () => onContactInfoClick(endUserData)
    }
  }

  if (type === ActionType.OUTREACH && canShowOutreachItems) {
    const label = 'Enroll in Outreach sequence'
    if (endUserData?.email) {
      let description = undefined
      if (!!numEnrolledOutreachSequences && numEnrolledOutreachSequences > 0) {
        description = `Currently enrolled in ${numEnrolledOutreachSequences} ${pluralize('sequence', numEnrolledOutreachSequences)}`
      } else if (numEnrolledOutreachSequences === 0) {
        description = 'Not currently enrolled in any sequences'
      }
      return {
        type,
        tooltipLabel: description ? `${label} (${description})` : label,
        overflowLabel: label,
        overflowDescription: description,
        onClick: onOutreachEnrollClick,
        actionIcon:
          numEnrolledOutreachSequences === undefined ? (
            <IconButton icon={type.toString()} onClick={onOutreachEnrollClick} />
          ) : (
            <Button
              onClick={onOutreachEnrollClick}
              className={styles.sequenceButton}
              type="utility">
              <OutreachEnrolledIcon
                className={cx(styles.sequenceStatus, {
                  [styles.sequenceUnenrolled]: numEnrolledOutreachSequences === 0
                })}
              />
            </Button>
          )
      }
    } else {
      return {
        type,
        tooltipLabel: NO_EMAIL_ADDRESS_MESSAGE,
        overflowLabel: label,
        overflowDescription: NO_EMAIL_ADDRESS_MESSAGE,
        disabled: true
      }
    }
  }

  if (type === ActionType.SALESLOFT && canShowSalesloftItems) {
    const label = 'Add to Salesloft cadence'
    if (endUserData?.email) {
      let description = undefined
      if (!!numEnrolledSalesloftCadences && numEnrolledSalesloftCadences > 0) {
        description = `Currently a member of ${numEnrolledSalesloftCadences} ${pluralize('sequence', numEnrolledSalesloftCadences)}`
      } else if (numEnrolledSalesloftCadences === 0) {
        description = 'Not currently a member of any cadences'
      }
      return {
        type,
        tooltipLabel: description ? `${label} (${description})` : label,
        overflowLabel: label,
        overflowDescription: description,
        onClick: onSalesloftEnrollClick,
        actionIcon:
          numEnrolledSalesloftCadences !== undefined ? (
            <IconButton icon={type.toString()} onClick={onSalesloftEnrollClick} />
          ) : (
            <Button
              onClick={onSalesloftEnrollClick}
              className={styles.sequenceButton}
              type="utility">
              <SalesloftEnrolledIcon
                className={cx(styles.sequenceStatus, {
                  [styles.sequenceUnenrolled]: numEnrolledSalesloftCadences === 0
                })}
              />
            </Button>
          )
      }
    } else {
      return {
        type,
        tooltipLabel: NO_EMAIL_ADDRESS_MESSAGE,
        overflowLabel: label,
        overflowDescription: NO_EMAIL_ADDRESS_MESSAGE,
        disabled: true
      }
    }
  }

  return null
}

export const ActivityActions = ({
  insight,
  endUserData,
  companyData,
  actionTypes = [ActionType.EMAIL, ActionType.LINKEDIN],
  className,
  numEnrolledOutreachSequences = undefined,
  numEnrolledSalesloftCadences = undefined,
}: {
  insight?: Insight
  endUserData: ActivityEndUser | undefined
  companyData: CompanyData | undefined
  actionTypes?: ActionType[]
  className?: string
  numEnrolledOutreachSequences?: number
  numEnrolledSalesloftCadences?: number
  trackAction?: (action: string, additionalData?: any) => void
}) => {
  const history = useHistory()
  const { id: accountId } = useParams<AccountDetailsPageParams>()
  const [enrollModalOpen, setEnrollModalOpen] = useState(false)
  const [modalDataProvider, setModalDataProvider] = useState<string>(ActionType.OUTREACH.toString())
  const enabledIntegrations = useContext(EnabledIntegrationsContext)

  const onEnrollClick = (provider: string) => {
    setModalDataProvider(provider)
    setEnrollModalOpen(true)
  }

  const onViewContactInfo = useCallback(
    (contact: ActivityEndUser) => {
      history.push({
        pathname: contactPath(contact.id),
        state: {
          navigatedFrom: 'Account details',
          navigatedFromId: accountPath(accountId)
        }
      })
    },
    [
      history,
      accountId
    ]
  )

  const actions = actionTypes
    .map(actionType =>
      buildActivityAction({
        accountId,
        insight,
        endUserData,
        companyData,
        type: actionType,
        onEnrollClick,
        onViewContactInfo,
        numEnrolledOutreachSequences,
        numEnrolledSalesloftCadences,
        outreachEnabled: enabledIntegrations.outreach
      })
    )
    .filter(Boolean)
  const visibleActions = actions.length > 3 ? actions.slice(0, 2) : actions
  const overflowActions = actions.length > 3 ? actions.slice(2) : []

  return (
    <div className={className} onClick={e => e.stopPropagation()}>
      {visibleActions.map(action => {
        return (
          action && (
            <ToolTip key={action.type} flow="left" render={() => <Div>{action.tooltipLabel}</Div>}>
              {action.actionIcon || (
                <IconButton
                  icon={action.type.toString()}
                  onClick={action.onClick}
                  disabled={action.disabled}
                />
              )}
            </ToolTip>
          )
        )
      })}
      {overflowActions.length > 0 && (
        <ActionDropdown
          className={styles.overflowActionDropdown}
          trigger={
            <ToolTip flow="left" render={() => 'More actions'}>
              <IconButton icon="meatballs" />
            </ToolTip>
          }
          align="right"
          actions={overflowActions.map(
            action =>
              action && {
                key: action.type,
                heading: true,
                label: action.overflowLabel,
                description: action.overflowDescription,
                disabled: action.disabled,
                action: action.onClick
              }
          )}
        />
      )}
      {/* Disable outreach in the EU */}
      {!isEU && enrollModalOpen && endUserData && (
        <EnrollModal
          onClose={() => setEnrollModalOpen(false)}
          endUserData={endUserData}
          useOutreach={modalDataProvider === ActionType.OUTREACH.toString()}
        />
      )}
    </div>
  )
}

export default ActivityActions
